import React from "react";

import { AccordionItemProps, ActionItem, IconItems, PlayerProp, PlayerStyle, Positions } from "../../components";
import { ball, cone, flag, goal, ladder } from "../../images";
import { SoccerPlayerLabelType } from "../../types";
import { getGlobalToolPane, toolPaletteShadingIds, toolPaletteTextBoxIds } from "../global";

/** ================================
 * Tool Palette Type Definitions
 ================================ */
/** ======== Player ======== */
export const scToolPalettePlayerIds = [
  "SC_PLAYER_1",
  "SC_PLAYER_2",
  "SC_PLAYER_3",
  "SC_PLAYER_4",
  "SC_PLAYER_5",
  "SC_PLAYER_6",
  "SC_PLAYER_7",
  "SC_PLAYER_8",
  "SC_PLAYER_9",
  "SC_PLAYER_10",
  "SC_PLAYER_11",
  "SC_PLAYER_POSSESSION_1",
  "SC_PLAYER_POSSESSION_2",
  "SC_PLAYER_POSSESSION_3",
  "SC_PLAYER_POSSESSION_4",
  "SC_PLAYER_POSSESSION_5",
  "SC_PLAYER_POSSESSION_6",
  "SC_PLAYER_POSSESSION_7",
  "SC_PLAYER_POSSESSION_8",
  "SC_PLAYER_POSSESSION_9",
  "SC_PLAYER_POSSESSION_10",
  "SC_PLAYER_POSSESSION_11",
  "SC_PLAYER_FILL_1",
  "SC_PLAYER_FILL_2",
  "SC_PLAYER_FILL_3",
  "SC_PLAYER_FILL_4",
  "SC_PLAYER_FILL_5",
  "SC_PLAYER_FILL_6",
  "SC_PLAYER_FILL_7",
  "SC_PLAYER_FILL_8",
  "SC_PLAYER_FILL_9",
  "SC_PLAYER_FILL_10",
  "SC_PLAYER_FILL_11",
] as const;
export type SC_ToolPalettePlayerId = (typeof scToolPalettePlayerIds)[number];
export const isSCPlayerId = (id: SC_ToolPaletteMarkerId): id is SC_ToolPalettePlayerId =>
  scToolPalettePlayerIds.includes(id as SC_ToolPalettePlayerId);

/** ======== Ball ======== */
export const scToolPaletteBallIds = ["SC_BALL"] as const;
export type SC_ToolPaletteBallId = (typeof scToolPaletteBallIds)[number];
export const isSCBallId = (id: SC_ToolPaletteMarkerId): id is SC_ToolPaletteBallId =>
  scToolPaletteBallIds.includes(id as SC_ToolPaletteBallId);

/** ======== Misc ======== */
export const scToolPaletteMiscIds = ["MISC_GOAL", "MISC_BALL", "MISC_CONE", "MISC_FLAG", "MISC_LADDER"] as const;
export type SC_ToolPaletteMiscId = (typeof scToolPaletteMiscIds)[number];
export const isSCMiscId = (id: SC_ToolPaletteMarkerId): id is SC_ToolPaletteMiscId =>
  scToolPaletteMiscIds.includes(id as SC_ToolPaletteMiscId);

/** ======== All Markers ======== */
export const scToolPaletteMarkerIds = [
  ...scToolPalettePlayerIds,
  ...scToolPaletteBallIds,
  ...scToolPaletteMiscIds,
  ...toolPaletteTextBoxIds,
  ...toolPaletteShadingIds,
] as const;
export type SC_ToolPaletteMarkerId = (typeof scToolPaletteMarkerIds)[number];
export const isSCMarkerId = (id: SC_ToolPaletteStencilId): id is SC_ToolPaletteMarkerId =>
  scToolPaletteMarkerIds.includes(id as SC_ToolPaletteMarkerId);

/** ======== Action ======== */
/** ======== DHO ======== */
export const scToolPaletteDHOIds = ["DHO"] as const;
export type SC_ToolPaletteDHOId = (typeof scToolPaletteDHOIds)[number];

export const scToolPaletteActionIds = [...scToolPaletteDHOIds] as const;
export type SC_ToolPaletteActionId = (typeof scToolPaletteActionIds)[number];

/** ======== All Stencils ======== */
export const scToolPaletteStencilIds = [...scToolPaletteMarkerIds, ...scToolPaletteActionIds] as const;
export type SC_ToolPaletteStencilId = (typeof scToolPaletteStencilIds)[number];
export const isStringSCToolPaletteStencilId = (id: string): id is SC_ToolPaletteStencilId =>
  scToolPaletteStencilIds.includes(id as SC_ToolPaletteStencilId);

/** ================================
 * Tool Palette Items
 ================================ */
/** ======== Players ======== */
export const getSoccerPlayerItems = (playerType: SoccerPlayerLabelType): PlayerProp<SC_ToolPalettePlayerId>[][] => {
  switch (playerType) {
    case "NUM":
      return [
        [
          { id: "SC_PLAYER_1", label: "1" },
          { id: "SC_PLAYER_2", label: "2" },
          { id: "SC_PLAYER_3", label: "3" },
          { id: "SC_PLAYER_4", label: "4" },
          { id: "SC_PLAYER_5", label: "5" },
          { id: "SC_PLAYER_6", label: "6" },
        ],
        [
          { id: "SC_PLAYER_7", label: "7" },
          { id: "SC_PLAYER_8", label: "8" },
          { id: "SC_PLAYER_9", label: "9" },
          { id: "SC_PLAYER_10", label: "10" },
          { id: "SC_PLAYER_11", label: "11" },
        ],
        [
          { id: "SC_PLAYER_POSSESSION_1", label: "1", style: PlayerStyle.OFFSET_POSSESSION },
          { id: "SC_PLAYER_POSSESSION_2", label: "2", style: PlayerStyle.OFFSET_POSSESSION },
          { id: "SC_PLAYER_POSSESSION_3", label: "3", style: PlayerStyle.OFFSET_POSSESSION },
          { id: "SC_PLAYER_POSSESSION_4", label: "4", style: PlayerStyle.OFFSET_POSSESSION },
          { id: "SC_PLAYER_POSSESSION_5", label: "5", style: PlayerStyle.OFFSET_POSSESSION },
          { id: "SC_PLAYER_POSSESSION_6", label: "6", style: PlayerStyle.OFFSET_POSSESSION },
        ],
        [
          { id: "SC_PLAYER_POSSESSION_7", label: "7", style: PlayerStyle.OFFSET_POSSESSION },
          { id: "SC_PLAYER_POSSESSION_8", label: "8", style: PlayerStyle.OFFSET_POSSESSION },
          { id: "SC_PLAYER_POSSESSION_9", label: "9", style: PlayerStyle.OFFSET_POSSESSION },
          { id: "SC_PLAYER_POSSESSION_10", label: "10", style: PlayerStyle.OFFSET_POSSESSION },
          { id: "SC_PLAYER_POSSESSION_11", label: "11", style: PlayerStyle.OFFSET_POSSESSION },
        ],
        [
          { id: "SC_PLAYER_FILL_1", label: "1", style: PlayerStyle.FILL },
          { id: "SC_PLAYER_FILL_2", label: "2", style: PlayerStyle.FILL },
          { id: "SC_PLAYER_FILL_3", label: "3", style: PlayerStyle.FILL },
          { id: "SC_PLAYER_FILL_4", label: "4", style: PlayerStyle.FILL },
          { id: "SC_PLAYER_FILL_5", label: "5", style: PlayerStyle.FILL },
          { id: "SC_PLAYER_FILL_6", label: "6", style: PlayerStyle.FILL },
        ],
        [
          { id: "SC_PLAYER_FILL_7", label: "7", style: PlayerStyle.FILL },
          { id: "SC_PLAYER_FILL_8", label: "8", style: PlayerStyle.FILL },
          { id: "SC_PLAYER_FILL_9", label: "9", style: PlayerStyle.FILL },
          { id: "SC_PLAYER_FILL_10", label: "10", style: PlayerStyle.FILL },
          { id: "SC_PLAYER_FILL_11", label: "11", style: PlayerStyle.FILL },
        ],
      ];
    case "POS":
      return [
        [
          { id: "SC_PLAYER_1", label: "D" },
          { id: "SC_PLAYER_5", label: "M" },
          { id: "SC_PLAYER_9", label: "F" },
          { id: "SC_PLAYER_11", label: "GK" },
        ],
        [
          { id: "SC_PLAYER_POSSESSION_1", label: "D", style: PlayerStyle.OFFSET_POSSESSION },
          { id: "SC_PLAYER_POSSESSION_5", label: "M", style: PlayerStyle.OFFSET_POSSESSION },
          { id: "SC_PLAYER_POSSESSION_9", label: "F", style: PlayerStyle.OFFSET_POSSESSION },
          { id: "SC_PLAYER_POSSESSION_11", label: "GK", style: PlayerStyle.OFFSET_POSSESSION },
        ],
        [
          { id: "SC_PLAYER_FILL_1", label: "D", style: PlayerStyle.FILL },
          { id: "SC_PLAYER_FILL_5", label: "M", style: PlayerStyle.FILL },
          { id: "SC_PLAYER_FILL_9", label: "F", style: PlayerStyle.FILL },
          { id: "SC_PLAYER_FILL_11", label: "GK", style: PlayerStyle.FILL },
        ],
      ];
    case "o":
      return [
        [
          { id: "SC_PLAYER_1", label: "o" },
          { id: "SC_PLAYER_POSSESSION_1", label: "o", style: PlayerStyle.OFFSET_POSSESSION },
          { id: "SC_PLAYER_FILL_1", label: "o", style: PlayerStyle.FILL },
        ],
      ];
  }
};

export const miscellaneousSoccerItems: ActionItem<SC_ToolPaletteMiscId>[] = [
  { id: "MISC_GOAL", label: "Goal", iconUrl: goal },
  { id: "MISC_BALL", label: "Ball", iconUrl: ball },
  { id: "MISC_CONE", label: "Cone", iconUrl: cone },
  { id: "MISC_FLAG", label: "Flag", iconUrl: flag },
  { id: "MISC_LADDER", label: "Ladder", iconUrl: ladder },
];

export interface GetSoccerToolPaletteProps {
  labelType: SoccerPlayerLabelType;
  onChange: (labelType: SoccerPlayerLabelType) => void;
}

export const getSoccerToolPalette = ({
  labelType,
  onChange,
}: GetSoccerToolPaletteProps): AccordionItemProps<SoccerPlayerLabelType>[] => [
  {
    type: "BUTTON_GROUP",
    id: "position",
    label: "Positions",
    buttonGroup: { options: ["NUM", "POS", "o"], selected: labelType, onChange },
    bodyRender: (
      <Positions positions={getSoccerPlayerItems(labelType)} spacing={labelType === "o" ? "center" : undefined} />
    ),
  },
  {
    type: "STANDARD",
    id: "miscellaneous",
    label: "Miscellaneous",
    bodyRender: <IconItems items={miscellaneousSoccerItems} />,
  },
  ...getGlobalToolPane<SoccerPlayerLabelType>(),
];
